"use client";
import React, { useState, useEffect, useRef } from "react";

interface Props {
    htmlContent: string;
}

export default function DisclaimerClient({ htmlContent }: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (textRef.current) {
            setShowButton(textRef.current.scrollHeight > textRef.current.clientHeight);
        }
    }, [htmlContent]);

    return (
        <div className="relative flex-1 w-full pb-4 p-6 rounded-2xl
                        bg-slate-800/40 border border-slate-700/50
                        hover:border-[#D61C4E]/20 transition-all duration-500
                        group/disc backdrop-blur-sm">

            {/* Disclaimer badge */}
            <div className="absolute -top-4 left-6 z-20 flex items-center">
                {/* Warning triangle */}
                <div className="relative z-30 shrink-0 -rotate-6 hover:rotate-0 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" className="w-9 h-9 drop-shadow-lg"
                        fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.0122 3.19302C12.3392 2.62886 13.1608 2.62886 13.4878 3.19302L21.4334 16.9032C21.7588 17.4646 21.3533 18.1667 20.7013 18.1667H3.29868C2.64672 18.1667 2.2412 17.4646 2.56658 16.9032L10.5122 3.19302H12.0122Z"
                            fill="#FBBF24" stroke="#FBBF24" strokeWidth="0.5" />
                        <rect x="11.5" y="7.5" width="2.5" height="6.5" rx="1.25" fill="white" />
                        <circle cx="12.75" cy="16.25" r="1.25" fill="white" />
                    </svg>
                </div>

                {/* Label pill */}
                <div className="relative -ml-3 pl-5 pr-4 py-2 bg-[#D61C4E] rounded-full
                                shadow-xl shadow-[#D61C4E]/30 border-t border-white/10
                                overflow-hidden group/badge hover:bg-[#b5163f] transition-colors">
                    {/* Shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                                    -translate-x-full group-hover/badge:translate-x-full transition-transform duration-700" />
                    <span className="relative z-10 text-white font-black text-[10px] uppercase tracking-[0.2em] leading-none">
                        Disclaimer
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="relative z-10 pt-3 flex flex-col items-start">
                <div
                    ref={textRef}
                    className={`text-[11px] leading-relaxed text-slate-400
                                group-hover/disc:text-slate-300 transition-colors italic text-left
                                ${isExpanded ? "line-clamp-none" : "line-clamp-2"}`}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />

                {showButton && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="mt-3 text-[9px] font-black uppercase tracking-[0.2em]
                                   text-[#D61C4E] hover:text-white transition-colors duration-200
                                   active:scale-95 transform"
                    >
                        {isExpanded ? "View Less ▲" : "See More ▼"}
                    </button>
                )}
            </div>
        </div>
    );
}
